// ============================================================================
// Emporio Zeva · Not-A-Salami — central brand content
// Everything below is approved client copy from the official Pitch Deck,
// One-Sheet, Ritual Postcard, Product Insert, and Tile Coaster artifacts.
// ============================================================================

export const BRAND = {
  name: "Not-A-Salami",
  parent: "Emporio Zeva",
  founder: "Eva",
  origin: "Modica, Sicily",
  made_in: "California",
  domain: "notasalami.com",
};

export const CONTACT = {
  email_primary: "hello@emporiozeva.com",
  email_hello: "hello@emporiozeva.com",
  phone: "+1 415-640-3005",
  phone_display: "415 · 640 · 3005",
  city: "San Francisco, CA",
  // Instagram is intentionally blank until the account is activated — the
  // footer/contact rows only render when a handle is set (via admin Settings).
  instagram: "",
};

export const TAGLINES = {
  primary: "A truly Sicilian treat. For the unexpected.",
  secondary: "A little peculiar, always delicious.",
  italian_tradition: "Italian Tradition. Crafted in California.",
  logistics: "We handle the logistics. You make the impression.",
};

// Five client-approved pillars from the corporate deck (slide 03)
export const PILLARS = [
  "Memorable",
  "Shareable",
  "Distinctive",
  "Refined",
  "Authentic",
];

// SLICE · SERVE · SAVOR · SHARE — from the Ritual Postcard
export const RITUAL = [
  {
    key: "serve",
    title: "Serve",
    body:
      "For the best flavor and texture, enjoy at room temperature. Remove from the fridge 15–20 minutes before serving.",
  },
  {
    key: "slice",
    title: "Slice",
    body:
      "Using a sharp knife, slice ½ inch thick. Each slice reveals its own beautiful pattern.",
  },
  {
    key: "share",
    title: "Share",
    body:
      "Made to be shared slowly, around good conversation and meaningful company.",
  },
  {
    key: "savor",
    title: "Savor",
    body:
      "Layers of rich cocoa, crunchy biscotti, chocolate chips, and delicate sugar crystals unfold with every bite.",
  },
];

// Eva's real letter from the Ritual Postcard (slightly broken for layout)
export const FOUNDER_LETTER = [
  "Inspired by a childhood memory of a sweet Sicilian confection shaped like a salame, Not-A-Salami was recreated years later in California as a way to bring back a feeling of warmth, home, and togetherness around the table.",
  "What began as a personal family tradition evolved into a handcrafted cocoa confection rooted in Sicilian heritage and thoughtfully reinterpreted in California.",
  "Unexpected in appearance yet deeply nostalgic at heart, Not-A-Salami is meant to create a moment of surprise, sharing, and conversation.",
  "Everything is produced in small batches in California with a hands-on approach to craftsmanship, quality, and presentation.",
];

// Corporate deck use cases (slide 05) — 6 real ones
export const CORPORATE_USE_CASES = [
  { title: "Client Appreciation", body: "Show key clients they're valued — beyond the holidays." },
  { title: "Employee Recognition", body: "Mark milestones, onboarding, and team achievements." },
  { title: "Deal Closing", body: "Celebrate a signed agreement with something memorable." },
  { title: "Holiday Gifting", body: "A distinctive alternative to traditional gifts." },
  { title: "Event & Conference", body: "A refined leave-behind that stands out." },
  { title: "Corporate Events", body: "Host gifts and shared table moments for company gatherings." },
  { title: "Office Parties", body: "A sliceable dessert that turns a team moment into a ritual." },
  { title: "Holiday Parties", body: "A year-round dessert that fits seasonal celebrations." },
  { title: "Thank You & Referrals", body: "A thoughtful gesture that strengthens relationships." },
];

// Customization options (slide 07) — 4 real
export const CUSTOMIZATION = [
  { title: "Signature Postcard", body: "A warm introduction to the Not-A-Salami experience from our team." },
  { title: "Personalized Sticker", body: "Your company identity discreetly integrated into the exterior presentation." },
  { title: "Sicilian Tile Coaster", body: "Customized with your company message for a more memorable and personal gifting moment." },
  { title: "NS Monogram Tissue Paper", body: "Subtle branded presentation designed to elevate the unboxing experience." },
];

// Card color treatments for pricing tiers. Shared by the corporate deck
// (slide 08) and the one-sheet so offers read as visually distinct. A tier
// can set an explicit `tone`; otherwise a badge marks the dark hero card.
export const TIER_TONES = {
  light: {
    card: "border-[#DFD7CA] bg-[#F9F6F0]",
    name: "text-[#5C4E4A]",
    title: "text-[#2A1F1D]",
    price: "text-[#C05A3A]",
    unit: "text-[#5C4E4A]",
    body: "text-[#5C4E4A]",
    inc: "text-[#5C4E4A]",
    bullet: "text-[#C05A3A]",
    min: "text-[#5C4E4A]",
    badge: "text-[#C05A3A]",
    dark: false,
  },
  dark: {
    card: "bg-[#2A1F1D] text-[#F9F6F0] border-[#2A1F1D]",
    name: "text-[#DFD7CA]",
    title: "text-[#F9F6F0]",
    price: "text-[#C05A3A]",
    unit: "text-[#DFD7CA]",
    body: "text-[#F9F6F0]",
    inc: "text-[#F9F6F0]",
    bullet: "text-[#C05A3A]",
    min: "text-[#B9935A]",
    badge: "text-[#C05A3A]",
    dark: true,
  },
  ember: {
    card: "bg-[#C05A3A] text-[#F9F6F0] border-[#C05A3A]",
    name: "text-[#F5EFE2]",
    title: "text-[#F9F6F0]",
    price: "text-[#F9F6F0]",
    unit: "text-[#F5EFE2]",
    body: "text-[#F9F6F0]",
    inc: "text-[#F9F6F0]",
    bullet: "text-[#F9F6F0]",
    min: "text-[#F5EFE2]",
    badge: "text-[#F5EFE2]",
    dark: true,
  },
};

// Resolve a tier's tone: explicit `tone` wins, then badge → dark, else light.
export function toneFor(pkg) {
  if (pkg?.tone) return pkg.tone;
  return pkg?.badge && String(pkg.badge).trim() ? "dark" : "light";
}

// Pricing tiers (slide 08) — REAL pricing, 24-unit case minimum
// Each tier carries a `tone` (light | dark | ember) that drives its card
// treatment so multiple offers stay visually distinct. `image` is an
// optional box photograph (filled from the Media library in admin).
export const CORPORATE_PACKAGES = [
  {
    name: "Curated",
    price: "$58",
    unit: "per unit",
    box: "White Box",
    blurb: "Designed for thoughtful corporate gifting.",
    includes: [
      "1× Not-A-Salami",
      "Signature Postcard",
      "Custom gift message",
      "NS monogram tissue paper",
    ],
    tone: "light",
    min: "Program begins at one case (24 units)",
  },
  {
    name: "Executive",
    price: "$78",
    unit: "per unit",
    box: "Black Box",
    blurb: "An elevated gifting experience with enhanced presentation.",
    includes: [
      "1× Not-A-Salami",
      "Signature Postcard",
      "Custom gift message",
      "Sicilian Tile Coaster",
      "Personalized logo sticker",
      "NS monogram tissue paper",
    ],
    badge: "Most chosen for institutional gifting",
    tone: "dark",
    min: "Program begins at one case (24 units)",
  },
  {
    name: "Il Mini",
    price: "TBD",
    unit: "per unit",
    box: "Mini Box",
    blurb:
      "A pocket-sized Not-A-Salami — half the size, all the ritual. Ideal for tasting flights and smaller gifting moments.",
    includes: [
      "1× Il Mini (Not-A-Salami)",
      "Signature Postcard",
      "Custom gift message",
      "NS monogram tissue paper",
    ],
    tone: "ember",
    min: "Program begins at one case (24 units)",
  },
];

// Fulfillment options (slide 09) — 4 real
export const FULFILLMENT = [
  { title: "Single Address Delivery", body: "Bulk delivery to one address — most cost-efficient per unit. Ideal for internal team distribution." },
  { title: "Multi-Recipient Delivery", body: "We ship direct to every recipient on your list. Perfect for remote teams or client appreciation." },
  { title: "Local Pickup", body: "Available for Bay Area clients. No shipping cost — coordinate directly with our team." },
  { title: "Rush Orders", body: "Expedited production and delivery available upon request. Surcharge applies." },
];

// Process steps (slide 10) — 4 real
export const PROCESS = [
  { n: "01", title: "Define Your Needs", body: "We align on your timeline, recipients, and gifting objectives." },
  { n: "02", title: "Select Your Gift Program", body: "Choose the format and volume that best fits your needs." },
  { n: "03", title: "We Execute", body: "From production to packaging to delivery, we manage the entire process." },
  { n: "04", title: "A Lasting Impression", body: "Your recipients receive a thoughtful, elevated gift that reflects your brand." },
];

// Existing testimonials & journal — kept as-is
export const TESTIMONIALS = [
  {
    quote:
      "Every bite of Not-A-Salami was a delightful journey back to Sicily. It's rare to find a treat that so perfectly captures the essence of traditional flavors with a modern twist.",
    name: "Fabio Ficano",
    role: "Returning Customer",
  },
  {
    quote:
      "The wrap, the paper, the look of it when open. The smell, like a cookie my mom used to make. The way it slices. The taste is SO good — I could NOT stop eating it.",
    name: "Lucinda Buxton Marin",
    role: "Pastry Chef",
  },
  {
    quote:
      "The most amazing, decadent, delicious chocolate salami I have ever tasted. Of course it helps that Eva is from Sicily and uses the finest chocolate. The highest rating of all.",
    name: "Maria C. Spurlock",
    role: "San Francisco",
  },
  {
    quote:
      "A revelation — rich, indulgent, and absolutely delicious. It's become a fast favorite in our home, perfect for sharing or enjoying solo. A standout treat.",
    name: "Melanie L.",
    role: "Local press",
  },
];

export const JOURNAL = [
  {
    slug: "a-sweet-journey-through-sicily",
    title: "Not-A-Salami: A Sweet Journey Through Sicily and Beyond",
    excerpt:
      "Not-A-Salami may seem like a modern culinary trend, but its roots run deep in the traditions of Sicilian holiday tables, where cocoa salami was made from what the pantry offered.",
    image: "/api/static/journal/sweet-journey-through-sicily.jpg",
    date: "Summer 2024",
    read: "6 min read",
    body: [
      "Long before Not-A-Salami was a brand, it was simply something Eva's grandmother made on the kitchen table in Modica. A wooden board, a long sheet of parchment paper, a length of twine. Cocoa folded into broken biscotti, butter, a measure of sugar, and whatever was in the pantry that week. Rolled by hand, tied at both ends, set in the cold corner of the cellar to firm up.",
      "It wasn't a recipe written down. It was the recipe — passed from one woman to another, slightly different in every house, never quite the same twice. In Sicilian, it had as many names as it had grandmothers. Salame di cioccolato. Salame turco. Salame del nonno. In our house, it was just la salame inglese — the English salami — for the way it sat on the table looking like one thing and revealing itself as another.",
      "The original use of the salami shape was practical, not theatrical. Cured meats hung from rafters; sweets were rolled to a similar diameter so they could be wrapped in the same paper and tied with the same twine, then placed alongside the prosciutto and the bresaola at the holidays. The wink was a happy accident. The reveal — that the dense, cool, fragrant slice was chocolate, not pork — became the joke that made the meal.",
      "We brought the recipe to San Francisco in our own kitchen, then a small commissary, then a slightly larger one. We tested cocoa from four continents and settled on a Modican blend that holds its temper even at room temperature. We tracked down biscotti with the precise crunch — not too sweet, not too soft. We argued about sugar crystals (yes, in the end). And then we did the only thing that mattered: we slowed down.",
      "Every Not-A-Salami is still rolled by hand, tied by hand, rested in our cold room for the same number of days. Each one travels in the same parchment and twine our family has used for three generations. When you cut it open at the table, what you're slicing is not a product — it's a small piece of a Sunday afternoon in Sicily, half a century ago, in a kitchen that smelled like coffee and cocoa and rain on the cobblestones.",
      "That's the journey. From Modica to a table in San Francisco — or yours, wherever it sits.",
    ],
  },
  {
    slug: "the-ultimate-gourmet-gift",
    title: "Elevate Your Dessert Game: Not-A-Salami as the Ultimate Gourmet Gift",
    excerpt:
      "Finding the perfect gift can be a challenge, especially when you want something unique and unforgettable. Here is why a sliceable cocoa confection belongs on every host's table.",
    image: "/api/static/site/hero-board.jpg",
    date: "Autumn 2024",
    read: "4 min read",
    body: [
      "The best gifts hold a secret. A wrapped object that hints at one thing and turns out to be another. A box that opens slowly. A name in a language the recipient doesn't quite speak yet. Generosity is, almost always, a small act of theater.",
      "Not-A-Salami was designed for exactly that. From the outside, it looks like a hand-tied cured meat — wrapped in parchment paper and gold foil, dusted in cocoa powder that reads as fresh pepper or fine mold. Most people, even the food-curious ones, miss the wink at first. They lift it, smell it, ask about the curing. Then someone slices it.",
      "What they find inside is the moment we built the whole thing around. A dense, cool interior speckled with crunchy biscotti, dark chocolate chips, and the faintest glitter of sugar crystals. Not too sweet. Not soft. Architectural. The room goes quiet for a second and then everyone laughs.",
      "We've heard this story dozens of times from corporate clients, hosts, restaurants. The cardiologist who unwrapped one at the office and watched colleagues stage an intervention. The wine bar that started slicing it alongside their cheese plate. The mother-in-law who hid hers in the fridge for two months and gave a slice to anyone she liked. (Apparently three people made the list.)",
      "It works as a gift because it does what gifts are supposed to do — it surprises, it photographs well, it travels well, it lasts about a week longer than it should, and it gives the recipient a story they'll tell for months. The unboxing alone tends to live on a phone for a while.",
      "For corporate gifting, we ship in white or black boxes with letterpressed serving cards, custom messages, and your logo tucked discreetly into the tissue. Eva packs each one. We deliver to one address or fifty. The story is the same either way: someone unwraps a salami, finds chocolate, and remembers who sent it.",
      "That's the whole pitch. It's a small thing, but it's a very good one.",
    ],
  },
  {
    slug: "rediscovering-a-classic",
    title: "A Nostalgic Journey: Rediscovering a Classic Treat",
    excerpt:
      "In a world where culinary trends come and go, there is something comforting about a classic treat that pulls you back to a kitchen you used to know.",
    image: "https://images.unsplash.com/photo-1481833761820-0509d3217039?auto=format&fit=crop&w=1600&q=80",
    date: "Winter 2024",
    read: "5 min read",
    body: [
      "There is a particular way that cocoa smells when it's being worked by hand, and if you grew up in a house where someone made dessert from scratch, you know it. Not the dry powder smell. The hydrated one. The smell of cocoa meeting warm butter, sugar dissolving, biscotti turning slightly soft at the edges. That smell does something to memory.",
      "When we started serving Not-A-Salami at small dinners in San Francisco, the most common reaction was not 'wow, what is this?' It was 'wait — this is the thing my grandmother used to make.' Italian-American guests would go silent for a moment and then start naming their nonnas. A guest from Buenos Aires recognized it as the salchichón de chocolate from her childhood. Someone from Lebanon called it lazy cake. The Polish version is similar enough that the recognition was instant.",
      "What we kept hearing was a version of the same sentence: 'I haven't had this in twenty years.' Sometimes thirty. Sometimes since their mother passed.",
      "We didn't set out to make something nostalgic. We set out to make something good — to honor the original Sicilian recipe and the technique it deserved. But food, more than almost anything else, is a vehicle for memory. A flavor you ate as a child is wired into the same neural circuits as your bedroom from age six. Bite into the right thing as an adult and you are eight years old again, in a kitchen that doesn't exist anymore.",
      "We think this is the quiet power of a classic treat done carefully. It doesn't compete with new desserts. It is, by design, older than the room you're standing in. It asks nothing of you except to slice it and pass it around.",
      "In a year when food trends seem to arrive and exit at speed, we are content to be making something one woman in our family was already making in 1962. The recipe has barely changed. The room around it has changed completely. That contrast is the point.",
      "Cut a slice. Pass it to your left. Tell whoever takes it about the kitchen you used to know.",
    ],
  },
];

export const PRESS = [
  "Selected by SF Made — Here & Now 2024",
  "Italian Tradition · Crafted in California",
  "Featured on local pastry chef tables",
  "Small batch · Hand rolled · San Francisco",
];

// Inline SVG badge — SFMade.org wordmark replacement (origin host suspended).
// Crisp at any size, no external dependency.
export const SF_MADE_BADGE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 220 64'>
       <rect width='220' height='64' rx='4' fill='#2A1F1D'/>
       <text x='110' y='27' text-anchor='middle' font-family='Newsreader, Georgia, serif' font-style='italic' font-size='22' fill='#F9F6F0'>SF Made</text>
       <line x1='40' y1='36' x2='180' y2='36' stroke='#C05A3A' stroke-width='1'/>
       <text x='110' y='52' text-anchor='middle' font-family='Inter, sans-serif' font-size='9' letter-spacing='3' fill='#B9935A'>HERE &amp; NOW · 2024</text>
     </svg>`
  );

// Unused today; kept pointing at the in-repo brand mark so nothing in the
// bundle reaches a third-party host.
export const LOGO_URL = "/brand/not-a-salami-logo-2026.png";

export const NOT_A_SALAMI_SEAL =
  "/brand/not-a-salami-logo-2026.png";

export const NOT_A_SALAMI_SEAL_LIGHT =
  "/brand/not-a-salami-logo-2026-light.png";

export const IMAGES = {
  // Shipped from the backend static folder so they survive every deploy and
  // don't depend on a third-party host.
  hero: "/api/static/site/hero-board.jpg",
  product: "/api/static/site/salami-board.jpg",
  // Eva's founder portrait for the Our Story page, shipped from the backend
  // static folder so it survives every deploy.
  founder: "/api/static/our-story/founder.jpg",
  sicily: "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?auto=format&fit=crop&w=1800&q=80",
  italian_moment: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&w=1400&q=80",
  // Branded "NOT-A-SALAMI" board
  gift: "/api/static/site/branded-board.jpg",
  // Deck slide imagery shipped from the backend static folder so they survive
  // every deploy and don't depend on third-party hotlinks.
  deck_slide2_tradition: "/api/static/decks/slide-2-tradition.jpg",
  deck_slide4_product: "/api/static/decks/slide-4-product.jpg",
  deck_slide6_expression: "/api/static/decks/slide-6-expression.jpg",
  split_blog: "/api/static/site/branded-board.jpg",
  journal_1: "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?auto=format&fit=crop&w=1600&q=80",
  nostalgia: "https://images.unsplash.com/photo-1481833761820-0509d3217039?auto=format&fit=crop&w=1600&q=80",
};
